import BaseService from '../../base/base_service';
import Event from '../../utils/event';
import {SYSTEM_EVENTS} from '../../events/events';
import {GEO_LOCATION} from './geo_consts';
import GeoHybrid from './geo_hybrid';
import DOM from '../../utils/dom';
import GeoDataFormatter from './geo_data_formatter';
import GeoPc from "./geo_pc";
import GeoLocationController
    from "../../components/options/option_components/geo_location/geo_location_component_controller";
import OptionsComponentController from "../../components/options/options_component_controller";
import IGeoService, {IGeoBase, IGeoLocationParams, IGeoRequestParams} from "./geo_service_interface";
import _ from "../../utils/underscore";
import { TimeSpan } from "@sparkware/app-utils";
import Device from "../../utils/device/device";
import ServiceFactory from "../service_factory";
import {IGeoParams} from "./geo_base";
import GeoOobee from "./geo_oobee";
import {GEO_LOCATION_KICK_REASONS, KICK_REASONS, KICK_TYPE} from "../player_messages_service/player_messages_consts";

const NONE = 'none';

const MAX_RETRIES_FOR_WAITING_PLAYER_MESSAGES_CONNECT = 50;
export default class GeoLocationService extends BaseService implements IGeoService {
    /**
     * @static
     * @type {string}
     */
    static CLASS = 'GeoLocationService';

    /**
     * @property
     * @type {object|null}
     * @private
     */
    private wrapper:IGeoBase = null;
    private geoLocationFreezeWindow:GeoLocationController = null;
    private geoDataFormatter:GeoDataFormatter = null;
    private geoLocationProcess:number = 0;
    private geoLocationTimeout:number = null;
    private restrictionChangedEvent:any = null;
    private initParams:IGeoLocationParams;
    private wrapperParams:IGeoParams;
    private requestTimeOut:TimeSpan;
    private waitingForResponse:boolean;
    private timeOutRef:number = null;
    private playerMessagesConnected: boolean = false;

    public init(params:IGeoLocationParams):void {
        super.init(params);
        this.initParams = params;
        if (this.isEnable() && this.isParamsValid(params)) {
            this.subscribe();
            this.wrapperParams = this.initWrapper(params);
            this.requestTimeOut = TimeSpan.parse(this.wrapperParams.waitTimeOut);
        }
    }

    private subscribe():void {
        this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this.onOauthChange);
        this.onSystemEvent(SYSTEM_EVENTS.GEO_GET_LOCATION, this.getGeoLocation);
        this.onSystemEvent(SYSTEM_EVENTS.ON_INIT_COMPLETE, this.onInitComplete);
        this.onSystemEvent(SYSTEM_EVENTS.PLAYER_MESSAGES_STRATEGY_READY, this.onPlayerMessagesConnected);
        this.onSystemEvent(SYSTEM_EVENTS.GEO_KICK_EVENT, (kickParams) => this.onGeoKickEvent(kickParams));
    }

    private onPlayerMessagesConnected() {
        this.playerMessagesConnected = true;
    }

    private onInitComplete():void {
        if (null !== this.wrapper) {
            this.wrapper.setParent(this);
            this.wrapper.runWrapper();
        }
        Event.fire(SYSTEM_EVENTS.GEO_HYBRID_UPDATE_ENABLE, this.isEnable());
    }

    public getWrapper():IGeoBase {
        return this.wrapper;
    }

    private getDeviceType():string{
        let type = "";
        if (Device.hybrid.isHybrid){
            if (Device.os.isIOS){
                type = GEO_LOCATION.GEO_HYBRID_IOS
            } else {
                type = GEO_LOCATION.GEO_HYBRID_ANDROID
            }
        } else if (Device.os.isIOS || Device.isProbablyIOSDevice){
            type = GEO_LOCATION.GEO_WEBAPP_IOS
        } else if (Device.os.isAndroid){
            type = GEO_LOCATION.GEO_WEBAPP_ANDROID
        }
        else {
            if (Device.os.isMacOS){
                type = GEO_LOCATION.GEO_MAC
            } else {
                type = GEO_LOCATION.GEO_PC
            }
        }
        return type
    }

    private initWrapper(params:IGeoLocationParams):IGeoParams {
        this.wrapper = null;
        let type:string = this.getDeviceType();
        let wrapperParams:IGeoParams;
        switch (type) {
           case GEO_LOCATION.GEO_HYBRID_ANDROID:
                wrapperParams = params.Hybrid.android;
                this.wrapper = GeoHybrid.create(wrapperParams);
                break;

           case GEO_LOCATION.GEO_HYBRID_IOS:
                wrapperParams = params.Hybrid.ios;
                this.wrapper = GeoHybrid.create(wrapperParams);
                break;

           case GEO_LOCATION.GEO_PC:
                wrapperParams = params.PC;
                wrapperParams.platform = GEO_LOCATION.WIN;
                this.wrapper = GeoPc.create(wrapperParams);
                break;

           case GEO_LOCATION.GEO_MAC:
                wrapperParams = params.PC;
                wrapperParams.platform = GEO_LOCATION.MAC;
                this.wrapper = GeoPc.create(wrapperParams);
                break;

           case GEO_LOCATION.GEO_WEBAPP_IOS:
                wrapperParams = params.Oobee;
                this.wrapper = GeoOobee.create(wrapperParams);
                break;

            case GEO_LOCATION.GEO_WEBAPP_ANDROID:
                wrapperParams = params.Oobee;
                this.wrapper = GeoOobee.create(wrapperParams);
                break;
        }
        return wrapperParams;
    }

    private isEnable():boolean {
        return this.initParams && this.initParams.enable;
    }

    private onOauthChange():void {
        if (this.serviceFactory.oAuth2Service().isAuthorized()) {
            this.startLoginGeoProcess(false);
        } else {
            this.playerMessagesConnected = false;
        }
    }

    public startLoginGeoProcess(bMandatory:boolean, reason: string = GEO_LOCATION.REASON_LOGIN):Promise<boolean> {

        let geo = this.serviceFactory.userService().geoLocationFromLogin();
        return this.startGeoProcess(geo, bMandatory, reason);
    }

    public updateGeoPeriodic(geo:IGeoRequestParams):Promise<boolean> {

        geo.isGeoLocationInProcess = true;
        return this.startGeoProcess(geo, true, GEO_LOCATION.REASON_PERIODIC);
    }

    private startGeoProcess(geo:IGeoRequestParams, isMandatory:boolean, reason:string):Promise<boolean> {
        if (!this.wrapper || !this.isEnable()) {
            this.sendAnalytics(GEO_LOCATION.GA_GEO_DISABLED, null);
            return Promise.resolve(false);//feature disabled - continue to load game if needed
        }
        if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
            this.logger.debug('Geo - user not logged-in - ignore not relevant status changes');
            return Promise.resolve(false);
        }
        if (this.waitingForResponse) {
            this.logger.debug('Geo - Already in the Geo Process - usually got on opening game with login process together - game opening should be canceled');
            return Promise.reject(false);
        }
        if (!geo || !geo.isGeoLocationInProcess) {
            this.sendAnalytics(GEO_LOCATION.GA_IS_GEO_LOCATION_IN_PROCESS_FALSE, null);

            return Promise.resolve(false);
        }
        let cid:string = this.serviceFactory.userService().getCID() || '';
        cid = cid.toString();// for iphone hybrid it should be explicitly string
        let geoDataFormatter:GeoDataFormatter = new GeoDataFormatter(geo, cid, isMandatory, reason);
        this.geoDataFormatter = geoDataFormatter;
        if (!geoDataFormatter.isDataValid()) {
            this.sendAnalytics(GEO_LOCATION.GA_GEO_NOT_VALID_REQUEST_FROM_PLATFORM, geoDataFormatter);
            return Promise.resolve(false);
        }

        this.sendAnalytics(GEO_LOCATION.GA_GEO_START_GEO_PROCESS, geoDataFormatter, {reasonToStartGeo: reason});
        return this.getGeoLocation(geoDataFormatter);
    }

    private isPlugInEnable(bMandatory:boolean):Promise<boolean> {
        return this.getWrapper().isPlugInEnable(bMandatory);
    }

    private getGeoLocationWhilePlayerMessagesReady(retryNumber: number, geoDataFormatter:GeoDataFormatter) {
        if(this.playerMessagesConnected === false && retryNumber < MAX_RETRIES_FOR_WAITING_PLAYER_MESSAGES_CONNECT) {
            setTimeout(()=> {
                this.getGeoLocationWhilePlayerMessagesReady(retryNumber + 1, geoDataFormatter);
                this.logger.debug('Geo location waiting for firebase connect. retry number:' + retryNumber);
            }, 400);
        } else {
            this.getWrapper().getGeoLocation(geoDataFormatter).then(() => {
                this.clearGeoLocationFromLogin();
            });

            this.setWaitingForResponse(true);
            this.sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_REQUEST_SENT, geoDataFormatter,{playerMessagesConnectRetries: retryNumber});
        }
    }

    private getGeoLocation(geoDataFormatter:GeoDataFormatter):Promise<boolean> {
            return this.isPlugInEnable(geoDataFormatter.isMandatory).then(() =>{
                let bStartGeo = false;
                this.sendAnalytics(GEO_LOCATION.GA_GEO_PLUGIN_ENABLE, geoDataFormatter,{waitingForResponse:this.waitingForResponse});
                if (!this.waitingForResponse) {
                    bStartGeo = true;
                    this.getGeoLocationWhilePlayerMessagesReady(0, geoDataFormatter);
                }
                if (bStartGeo) {
                    if (!geoDataFormatter.isPeriodic) {
                        this.closeGame();// if game is already opened, it should be explicitly closed (in case of play "real money" button from "demo mode")
                        this.showFreezeWindow();
                    }
                }
                return Promise.reject(true); // cancel game opening process (geo step)
        });
    }

    private showFreezeWindow():void {
        this.geoLocationFreezeWindow = this.geoLocationFreezeWindow || OptionsComponentController.create().load('GeoLocation');
        this.geoLocationFreezeWindow.render();
        this.geoLocationProcess = new Date().getTime();
        this.sendAnalytics(GEO_LOCATION.GA_GEO_SHOW_GEO_IN_PROGRESS_POPUP, this.geoDataFormatter);

        this.startFreezeWindowTimeout();
    }

    private startFreezeWindowTimeout():void{
        this.stopFreezeWindowTimeout();
        this.geoLocationTimeout = DOM.setTimeout(() => this.closeControllerOnTimeout(), this.initParams.geoProcessTimeout);// starting geo location Timeout (not longer then ~60 sec)
    }

    private stopFreezeWindowTimeout():void{
        if (this.geoLocationTimeout) {
            DOM.clearTimeout(this.geoLocationTimeout);
        }
    }

    public restartFreezeWindowIfNeeded():void {
        if (!this.geoDataFormatter.isPeriodic){
            this.showFreezeWindow();
        }
    }

    private closeControllerOnTimeout() {
        this.closeFreezeWindow();
        this.sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_FAIL_TIMEOUT_ON_UPDATING_RESTRICTIONS, this.geoDataFormatter);
        this.showGeneralTechnicalError();
    }

    private showGeneralTechnicalError() {
        let message = {text: this.serviceFactory.languageService().translation(GEO_LOCATION.TECHNICAL_PROBLEM_REASON),
            title: this.serviceFactory.languageService().translation(GEO_LOCATION.GEO_IN_PROCESS_MSG_TITLE),
            closeButton: true,
            buttons: [
                {name: this.serviceFactory.languageService().translation(GEO_LOCATION.GENERIC_OK_BUTTON), isClosable: true, callback: () => {
                        this.closeGame();
                    }}
            ],
            btnCloseCallback: () => {
                this.closeGame();
            }};
        Event.fire(SYSTEM_EVENTS.MESSAGE_OPEN, message);
    }

    private closeGame() {
        this.serviceFactory.gameService().closeRunningGame();
    }

    public onGeoLocationResOk(geoLocationEncryptedResponse:string):void {
        this.sendAnalytics(GEO_LOCATION.GA_GEO_GEO_COMPLY_RESULT_OK, this.geoDataFormatter);
        this.validateGeoLocation(true, geoLocationEncryptedResponse);
    }

    public onGeoLocationResFailed(errorCode:number, errorMessage:string =''):void {
        this.sendAnalytics(GEO_LOCATION.GA_GEO_GEO_COMPLY_RESULT_FAILED, this.geoDataFormatter, errorMessage);
        this.validateGeoLocation(false, '', errorCode, errorMessage);
    }

    private clearGeoLocationFromLogin(){
        this.serviceFactory.userService().clearGeoLocationFromLogin();
        this.sendAnalytics(GEO_LOCATION.GA_GEO_CLEAN_GEO_LOGIN_PROPERTY, this.geoDataFormatter);
    }

    private validateGeoLocation(resOk:boolean, msg:string, errorCode = -1, errorMsg = ''):void {
        this.setWaitingForResponse(false);
        this.sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_VALIDATION, this.geoDataFormatter, this.resultForAnalitics(resOk, errorCode, errorMsg));
        if (!this.restrictionChangedEvent) {
            this.restrictionChangedEvent = this.onSystemEvent(SYSTEM_EVENTS.RESTRICTIONS_CHANGED, () => this.closeFreezeWindow());// normal closing geo message on getting restriction response
        }
        this.serviceFactory.userService().validateGeoLocation(msg, this.geoDataFormatter.isPeriodic, errorCode, errorMsg).then((data) => {
            this.sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_SUCCEEDED_VALIDATE_GEO_LOCATION, this.geoDataFormatter, this.resultForAnalitics(resOk, errorCode, errorMsg));
            if(_.isNullOrUndefined(data.NextIntervalInSeconds)) {
                Event.fire(SYSTEM_EVENTS.GEO_LOCATION_FAILED);
            }
        }).catch(() => {
            this.closeFreezeWindow();// in case of validateGeoLocation error
            this.sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_FAIL_VALIDATE_GEO_LOCATION, this.geoDataFormatter, this.resultForAnalitics(resOk, errorCode, errorMsg));
            this.showGeneralTechnicalError();
            Event.fire(SYSTEM_EVENTS.GEO_LOCATION_FAILED);
        });
    }

    private resultForAnalitics(resOk:boolean, errorCode:number, errorMsg:string):any {
        return {result:resOk, errorCode:errorCode, errorMsg:errorCode}
    }

    private sendAnalytics(action:string, geoData:GeoDataFormatter, additionalData:any=null):void {

        let wrapper:string = this.getWrapper() ? this.getWrapper().constructor.name : NONE;
        let data = {
            isMandatory: geoData ? geoData.isMandatory : NONE,
            isPeriodicFlow: geoData ? geoData.isPeriodic : NONE,
            isWaitingForResponse: geoData ? this.waitingForResponse : NONE,
            isDataValid: geoData ? geoData.isDataValid() : NONE,
            wrapper: wrapper
        };
        data = Object.assign(data, additionalData);
        let analyticsData:any = {
            category: GEO_LOCATION.GA_GEO_LOCATION_CATEGORY_SERVICE,
            action: action
        };
        if (additionalData){
            analyticsData.geoData = data;
        }
        this.logger.debug(action, JSON.stringify(analyticsData));
        this.serviceFactory.structuredLoggingService().getLogger(analyticsData).info();
    }

    public closeFreezeWindow():void {
        this.stopFreezeWindowTimeout();
        if (this.restrictionChangedEvent) {
               this.restrictionChangedEvent.off();
               this.restrictionChangedEvent = null;
            }
        if (this.geoLocationFreezeWindow) {
            let closeDelay = this.initParams.antiFlickerTimer - (new Date().getTime() - this.geoLocationProcess);
            if (closeDelay > 0) {
                DOM.setTimeout(() => this.doCloseFreezeWindow(), closeDelay);// prevent closing freeze window on too short interval (<5 sec)
            } else {
                this.doCloseFreezeWindow();
            }
        }
    }

    private doCloseFreezeWindow():void {
        let geoProcessTime:string = String(new Date().getTime() - this.geoLocationProcess);
        this.sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_POPUP_CLOSED,this.geoDataFormatter,{geoProcessTime:geoProcessTime});
        this.geoLocationFreezeWindow.close();
        this.geoLocationFreezeWindow = null;
    }

    private isParamsValid(params):boolean {
        return !(_.isNullOrUndefined(params) || _.isNullOrUndefined(params.Hybrid) || _.isNullOrUndefined(params.PC));
    }

    private setWaitingForResponse(value:boolean):void {
        this.waitingForResponse = value;
        this.clearTimeOutRef();
        if (value) {
            this.timeOutRef = DOM.setTimeout(() => {
                this.sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_RESET_WAITING_FOR_RESPONSE, this.geoDataFormatter,{requestTimeOut:this.requestTimeOut});
                this.waitingForResponse = false;
            }, this.requestTimeOut.totalMilliseconds);
        }
    }

    private clearTimeOutRef():void {
        if (this.timeOutRef) {
            DOM.clearTimeout(this.timeOutRef);
            this.timeOutRef = null;
        }
    }

    public getServiceFactory():ServiceFactory{
        return this.serviceFactory;
    }

    onGeoKickEvent(kickParams) {
        let message: any = '';

        if (!_.isObject(kickParams) || kickParams.KickType !== KICK_TYPE.SOFT_KICK) {
            return;
        }


        if (this.getIfKnownKickReason(kickParams.KickReasonId)) {
            //In CIP8 Mini Orbit we just want to close the game, Poker will show the message
            if(Device.os.isMobileOS && this.serviceFactory.integrationService().isMiniOrbit()) {
                this.closeGame();
                return;
            }

            //In Casino we will show the message to the user that he got click and close the game
            message = {
                text: this.serviceFactory.languageService().translation('kick_reason_' + kickParams.KickReasonId),
                customCSSClassList: ['cgp-mdp-text-support-slash-n']
            };
            if (this.getIfKnownGeoLockationKickReason(kickParams.KickReasonId)) {
                this.sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_KICK_EVENT, this.geoDataFormatter, kickParams);

                //if detailed messages were received, then overwrite default kick text with the ones received
                if (kickParams.DetailedMessages?.length > 0) {
                    message.text = this._getDetailedMessagesList(kickParams.DetailedMessages);
                };

                Object.assign(message, {
                    title: this.serviceFactory.languageService().translation(GEO_LOCATION.GEO_IN_PROCESS_MSG_TITLE),
                    closeButton: true,
                    buttons: [
                        {name: this.serviceFactory.languageService().translation(GEO_LOCATION.GENERIC_OK_BUTTON), isClosable: true, callback: () => {
                                this.closeGame();
                            }}
                    ],
                    btnCloseCallback: () => {
                        this.closeGame();
                    }
                });
            }

            //We fire the message only if it is known Kick Reason
            if (message) {
                Event.fire(SYSTEM_EVENTS.MESSAGE_OPEN, message);
            }

        }else {
            this.logger.debug('onGeoKickEvent - Kick was ignore since it is not a un known Kick Reason ID ');
            this.sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_KICK_EVENT_UN_KNOWN_REASON_ID, this.geoDataFormatter, kickParams);
        }

    }

    private getIfKnownKickReason(reasonId) {
        return KICK_REASONS.includes(reasonId);
    }

    private getIfKnownGeoLockationKickReason(reasonId) {
        return GEO_LOCATION_KICK_REASONS.includes(reasonId);
    }

    private _getDetailedMessagesList(messages: string[]): string {
        return messages.join('\n\n');
    }
  }